import React, { useMemo, useState } from 'react';
import { Autocomplete, Chip, TextField, TextFieldProps } from '@mui/material';
import {
  useAddTagByTypeMutation,
  useTagsByTypeQuery,
} from '../../generated/graphql';

export type TagAutocompleteProps = {
  id: string;
  label?: string;
  typeKey: string; // e.g., 'tour' | 'route' | 'leg' | 'cost'
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  size?: 'small' | 'medium';
  variant?: TextFieldProps['variant'];
  sx?: any;
};

export const TagAutocomplete: React.FC<TagAutocompleteProps> = ({
  id,
  label,
  typeKey,
  value,
  onChange,
  disabled = false,
  placeholder = 'Add tag',
  ariaLabel,
  size = 'small',
  variant = 'standard',
  sx,
}) => {
  const [inputValue, setInputValue] = useState('');
  const showTagSuggestions = useMemo(
    () => inputValue.trim().length >= 2,
    [inputValue],
  );

  const { data } = useTagsByTypeQuery({
    variables: { type: typeKey },
    fetchPolicy: 'cache-and-network',
  });
  const [addTagByType] = useAddTagByTypeMutation();
  const options: string[] = useMemo(
    () =>
      Array.isArray(data?.tagsByType) ? (data!.tagsByType as string[]) : [],
    [data?.tagsByType],
  );

  const filterTagOptions = (
    opts: readonly string[],
    state: { inputValue: string },
  ): string[] => {
    const q = (state.inputValue || '').toString().trim().toUpperCase();
    if (q.length < 2) return [];
    return (opts as string[]).filter((opt) =>
      (opt || '').toString().toUpperCase().includes(q),
    );
  };

  const normalize = (arr: string[]) =>
    Array.from(
      new Set(
        arr
          .map((v) => (v || '').toString().trim().toUpperCase())
          .filter(Boolean),
      ),
    );

  const handleChange = async (_e: any, nextValues: string[]) => {
    if (disabled) return;
    const normalized = normalize(nextValues);
    const added = normalized.filter((v) => !value.includes(v));
    onChange(normalized);

    if (added.length > 0) {
      // Persist any new tags that aren't in options yet, one by one
      const newOnes = added.filter((t) => !options.includes(t));
      for (const t of newOnes) {
        try {
          await addTagByType({ variables: { type: typeKey, tag: t } });
        } catch {
          // ignore persistence errors
        }
      }
    }
  };

  return (
    <Autocomplete
      id={id}
      multiple
      freeSolo
      options={options}
      filterOptions={filterTagOptions}
      open={!disabled && showTagSuggestions}
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_e, newInput) => {
        if (!disabled) setInputValue((newInput || '').toString().toUpperCase());
      }}
      renderTags={(vals: readonly string[], getTagProps) =>
        vals
          .filter((option: string) => option && option.trim() !== '')
          .map((option: string, index: number) => (
            <Chip
              variant="outlined"
              label={option}
              {...getTagProps({ index })}
              disabled={disabled}
              size="small"
              sx={{ m: 0.25 }}
              key={`${option}-${index}`}
            />
          ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          hiddenLabel={!label}
          variant={variant}
          placeholder={placeholder}
          disabled={disabled}
          inputProps={{
            ...params.inputProps,
            'aria-label': ariaLabel || label || placeholder,
            style: {
              ...((params.inputProps as any).style || {}),
              textTransform: 'uppercase',
            },
          }}
          size={size}
        />
      )}
      size={size}
      sx={sx}
    />
  );
};

export default TagAutocomplete;
